import { Box, Divider, Flex, FormControl, FormLabel, HStack, Input, Switch, Text, VStack } from '@chakra-ui/react';
import { MAP_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getMapTypeForEvent } from 'libs/third-party/Mixpanel/mixpanel.helper';
import cloneDeep from 'lodash.clonedeep';
import { StepColorPickerContainer } from 'modules/Editor/components/ColorPicker';
import {
  ChartSettingsColorPicker,
  ChartSettingsDivider,
  ChartSettingsEntry,
  ChartSliderInput,
  SettingsAccordion,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { TRIGGER_BUTTON_SIZE } from 'modules/common/components/ColorPicker/ColorPicker.config';
import { StepColorPickerStyle } from 'modules/common/components/ColorPicker/ColorPicker.style';
import { DropdownPopover, stopPropagationHandler } from 'modules/common/components/ToolbarPopover';
import {
  ChangeEvent,
  FocusEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Color } from 'types/basic.types';
import { getAlpha } from 'utils/color';
import { StepSeriesColor } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommonMenuSettings';
import { useWidget } from 'widgets/sdk';

import {
  COLOR_STEPS_OPTIONS,
  COLOR_STEPS_OPTIONS_OFFSET,
  DEFAULT_BUBBLE_BACKGROUND_COLOR,
  DEFAULT_BUBBLE_COLOR,
  DEFAULT_COLOR_STEPS,
  DEFAULT_MONOCHROME_BACKGROUND_COLOR,
  PRESET_PALETTE,
} from '../MapWidget.config';
import { generateDataClasses, getUpdatedColors, getUpdatedDataClasses, validateSeriesData } from '../MapWidget.helpers';
import {
  ColorPaletteButtonProps,
  ColorPaletteName,
  ColorStepsDisplayProps,
  MapWidgetData,
  Range,
  RangeInputProps,
  ScaleType,
} from '../MapWidget.types';
import { validateIncomingValue } from './MapWidgetSettings.helpers';

export const MapSettings = () => {
  const { mapType } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'mapChartSettings' });

  return (
    <SettingsAccordion isDefaultOpen={true} title={t('mapStyleTitle')}>
      {mapType === MapType.Choropleth && (
        <>
          <StepSeriesColor />
          <ColorSteps />
          <CustomizeRange />
          <ChartSettingsDivider />
          <ColorPalette />
        </>
      )}
      {mapType === MapType.Bubble && (
        <>
          <BubbleBackgroundColor />
          <BubbleColor />
        </>
      )}
      {mapType === MapType.Monochrome && (
        <>
          <MonochromeBackgroundColor />
        </>
      )}
      <ChartSettingsDivider />
      <NoDataColor />
      <ChartSettingsDivider />
      <BorderColor />
      <BorderWidth />
    </SettingsAccordion>
  );
};

const CustomizeRange = () => {
  const {
    mapType,
    scaleType = ScaleType.Steps,
    seriesData,
    seriesMeta: { dataClasses, colorSteps = DEFAULT_COLOR_STEPS, isCustomizeRangeEnabled = false },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const [localDataClasses, setLocalDataClasses] = useState<Range[]>([]);
  const [isFirstUpdate, setIsFirstUpdate] = useState(true);

  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'mapChartSettings' });
  const label = t('customizeRangeLabel');
  const switchId = label.replaceAll(' ', '-').toLowerCase() + '-toggle' + useId();

  const { safeSeriesData, seriesValues } = useMemo(() => validateSeriesData(seriesData), [seriesData]);

  // useUpdateEffect is not used because it misses the first render for setLocalDataClasses.
  useEffect(() => {
    if (isCustomizeRangeEnabled || isFirstUpdate) {
      if (dataClasses === undefined) {
        setLocalDataClasses(generateDataClasses(colorSteps, seriesValues));
      } else {
        setLocalDataClasses(cloneDeep(dataClasses));
      }
    }
  }, [colorSteps, seriesValues, dataClasses, isCustomizeRangeEnabled, isFirstUpdate, safeSeriesData]);

  const onCustomizeRangeToggle = useCallback(() => {
    const nextToggleState = !isCustomizeRangeEnabled;
    updateWidget({
      seriesMeta: {
        isCustomizeRangeEnabled: nextToggleState,
        dataClasses: getUpdatedDataClasses(dataClasses, colorSteps, nextToggleState, safeSeriesData),
      },
    });
    setIsFirstUpdate(false);
    Mixpanel.track(MAP_EDITED.eventName, {
      styling_property: MAP_EDITED.stylingProperty.MapStyle,
      styling_value: nextToggleState ? MAP_EDITED.stylingValue.CustomRangeOn : MAP_EDITED.stylingValue.CustomRangeOff,
      map_type: getMapTypeForEvent(MAP_EDITED, mapType, scaleType),
    });
  }, [colorSteps, dataClasses, isCustomizeRangeEnabled, mapType, scaleType, safeSeriesData, updateWidget]);

  const onValueChange = useCallback(
    (
      dataClassesIndex: number,
      propName: keyof Range,
      event: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
    ) => {
      const parsedIncomingValue = validateIncomingValue(
        (event.target as HTMLInputElement).value,
        dataClassesIndex,
        localDataClasses.length,
        propName,
      );

      setLocalDataClasses((prevDataClasses) => {
        const updatedDataClasses = prevDataClasses.map((data, index) => {
          if (index === dataClassesIndex) {
            return {
              ...data,
              [propName]: parsedIncomingValue,
            };
          }
          return data;
        });
        updateWidget({
          seriesMeta: { dataClasses: updatedDataClasses },
        });
        return updatedDataClasses;
      });
    },
    [localDataClasses.length, updateWidget],
  );

  return (
    <>
      <Flex alignItems='center' gap={3} justifyContent='space-between' color='font.500' fontSize='sm' py='2' mt='2'>
        <FormControl display='flex' alignItems='center'>
          <FormLabel htmlFor={switchId} mr='auto' fontSize='sm' fontWeight='400' mb='0'>
            {label}
          </FormLabel>
          <Switch onChange={onCustomizeRangeToggle} isChecked={isCustomizeRangeEnabled} id={switchId} />
        </FormControl>
      </Flex>
      {isCustomizeRangeEnabled && (
        <VStack marginTop={2} marginBottom={4} align={'end'}>
          {localDataClasses.map((data, index) => (
            <HStack gap={'9px'} key={index}>
              <RangeInput
                value={data.from ?? ''}
                onChange={(value) => onValueChange(index, 'from', value)}
                placeholder='Min'
                ariaLabel={t('minimumRangeInput')}
              />
              <Divider w={3.5} bgColor={'gray.500'} opacity={1} />
              <RangeInput
                value={data.to ?? ''}
                onChange={(value) => onValueChange(index, 'to', value)}
                placeholder='Max'
                ariaLabel={t('maximumRangeInput')}
              />
            </HStack>
          ))}
        </VStack>
      )}
    </>
  );
};

const RangeInput = ({ value, onChange, placeholder, ariaLabel }: RangeInputProps) => {
  const [inputValue, setInputValue] = useState<string | number | readonly string[] | undefined>('');

  useEffect(() => {
    setInputValue(value ?? ''); // If value is undefined, set inputValue to an empty string
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    if (event.target.value !== value?.toString()) {
      onChange(event);
    }
  };

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      const inputValue = (event.target as HTMLInputElement).value;
      if (inputValue !== value?.toString()) {
        onChange(event);
      }
    }
  };

  // Chakra NumberInput and NumberInputField unused due to the issue below.
  // Issue: When backspacing in the Chakra NumberInput, it behaves as expected by removing each digit one-by-one.
  // However, when the last numeric digit is being removed, it automatically reverts to "0" as it treats it as the smallest acceptable numeric value.
  // This requires an additional backspace to remove the "0" and achieve an empty string in the input field.

  // NumberInputField also has an issue with "e" input: https://github.com/chakra-ui/chakra-ui/issues/7633
  return (
    <Input
      textAlign={'center'}
      w={78}
      h={7}
      fontSize={'sm'}
      placeholder={placeholder}
      type={'number'}
      aria-label={ariaLabel}
      value={inputValue} // Use the local state for the value
      onChange={handleInputChange} // Update the local state when input changes
      onBlur={handleBlur} // Call onChange with the updated value on blur
      onKeyDown={onInputKeyDown}
    />
  );
};

const ColorSteps = () => {
  const {
    seriesData,
    seriesMeta: { colors, dataClasses, colorSteps = DEFAULT_COLOR_STEPS, isCustomizeRangeEnabled = false },
    paletteColorName,
    updateWidget,
  } = useWidget<MapWidgetData>();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'mapChartSettings' });

  // The update of color steps count changes these:
  // - The amount of legend labels in the diagram
  // - The amount of colors applicable for the legend labels.
  const onChangeColorSteps = useCallback(
    (steps: string) => {
      const newColorSteps = Number(steps) + COLOR_STEPS_OPTIONS_OFFSET;
      updateWidget({
        seriesMeta: {
          colorSteps: newColorSteps,
          colors: getUpdatedColors(colors, paletteColorName, newColorSteps),
          dataClasses: getUpdatedDataClasses(dataClasses, newColorSteps, isCustomizeRangeEnabled, seriesData),
        },
      });
    },
    [colors, dataClasses, isCustomizeRangeEnabled, paletteColorName, seriesData, updateWidget],
  );

  // No need to check the dependency array as the handler has no dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyPress = useCallback(stopPropagationHandler(triggerRef), []);

  return (
    <ChartSettingsEntry label={t('numberOfStepsLabel')} containerProps={{ marginTop: 8, marginBottom: 2 }}>
      <DropdownPopover
        label={t('numberOfStepsTooltip')}
        optionProps={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        containerProps={{
          onKeyDown: handleKeyPress,
          onKeyUp: handleKeyPress,
          'data-testid': 'brand-selector',
        }}
        triggerProps={{
          'data-testid': 'color-steps-trigger',
          ref: triggerRef,
          sx: {
            '& p': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
        }}
        buttonOptionProps={{
          h: 7,
        }}
        closeOnSelect
        placement={'bottom'}
        returnFocusOnClose={false}
        triggerSelectOnSameValue={false}
        selectedIndex={colorSteps - COLOR_STEPS_OPTIONS_OFFSET}
        onSelect={onChangeColorSteps}
        options={COLOR_STEPS_OPTIONS}
        useLabelInsteadOfSelectedLabel
      />
    </ChartSettingsEntry>
  );
};

const BubbleColor = () => {
  const {
    seriesMeta: { bubbleColor },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const onBubbleColorChange = (incomingColor: string) => {
    // If a hexadecimal color has no alpha channel, conventionally it means alpha: 1, opacity: 100%.
    // However, when passing colors without an alpha channel to the map bubble, it defaults to 50% opacity.
    // To ensure consistent behavior, we parse and modify the color by adding a 100% alpha channel ("FF").
    const bubbleColor = getAlpha(incomingColor) === 1 ? `${incomingColor}FF` : incomingColor;
    updateWidget({ seriesMeta: { bubbleColor } });
  };

  return (
    <ChartSettingsColorPicker
      label={t('bubbleColorLabel')}
      color={typeof bubbleColor === 'string' ? bubbleColor : DEFAULT_BUBBLE_COLOR}
      onChange={onBubbleColorChange}
      showNoColorChecker={true}
    />
  );
};

const BubbleBackgroundColor = () => {
  const {
    seriesMeta: { bubbleBackgroundColor },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const onBackgroundColorChange = (bubbleBackgroundColor: string) =>
    updateWidget({ seriesMeta: { bubbleBackgroundColor } });

  return (
    <ChartSettingsColorPicker
      label={t('bubbleBackgroundColorLabel')}
      color={typeof bubbleBackgroundColor === 'string' ? bubbleBackgroundColor : DEFAULT_BUBBLE_BACKGROUND_COLOR}
      onChange={onBackgroundColorChange}
      showNoColorChecker={true}
    />
  );
};

const MonochromeBackgroundColor = () => {
  const {
    seriesMeta: { monochromeBackgroundColor },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const onBackgroundColorChange = (monochromeBackgroundColor: string) =>
    updateWidget({ seriesMeta: { monochromeBackgroundColor } });

  return (
    <ChartSettingsColorPicker
      label={t('bubbleBackgroundColorLabel')}
      color={
        typeof monochromeBackgroundColor === 'string' ? monochromeBackgroundColor : DEFAULT_MONOCHROME_BACKGROUND_COLOR
      }
      onChange={onBackgroundColorChange}
      showNoColorChecker={true}
    />
  );
};

// Todo: If `Map Style: Scale Type is Gradient
const ColorPalette = () => {
  const {
    mapType,
    scaleType = ScaleType.Steps,
    seriesMeta: { colorSteps },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const onClick = (colors: string[], paletteColorName: ColorPaletteName) => {
    updateWidget({
      seriesMeta: { colors },
      paletteColorName,
    });
    Mixpanel.track(MAP_EDITED.eventName, {
      styling_property: MAP_EDITED.stylingProperty.MapStyle,
      styling_value: MAP_EDITED.stylingValue.ColorPresets,
      map_type: getMapTypeForEvent(MAP_EDITED, mapType, scaleType),
    });
  };

  return (
    <VStack>
      <Text fontSize={'sm'} alignSelf={'start'}>
        {t('colorPresetsLabel')}
      </Text>
      <Flex flexDirection={'column'} gap={2} width={'100%'}>
        {PRESET_PALETTE.map((preset, index) => (
          <Flex flexDirection={'row'} padding={1} gap={4} key={index}>
            <Text paddingY={1} width={28} fontSize={12}>
              {t(`colorPalette.${preset.label}` as keyof typeof t)}
            </Text>
            <ColorPaletteButton colorPreset={preset} colorSteps={colorSteps} onClick={onClick} />
          </Flex>
        ))}
      </Flex>
    </VStack>
  );
};

const ColorPaletteButton = ({ colorPreset, colorSteps, onClick }: ColorPaletteButtonProps) => {
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const colors: Color[] = colorPreset.colors[colorSteps] || [];
  const colorPaletteName = colorPreset.label;
  const onColorPaletteButtonClick = () => onClick(colors, colorPaletteName);

  return (
    <StepColorPickerContainer
      onClick={onColorPaletteButtonClick}
      as='button'
      aria-label={t('colorPaletteButtonAriaLabel', {
        colorPaletteName: t(`colorPalette.${colorPaletteName}` as keyof typeof t),
      })}
    >
      {colors.map((color, index) => (
        <ColorStepsDisplay key={index} color={color} />
      ))}
    </StepColorPickerContainer>
  );
};

const BorderWidth = () => {
  const {
    series: { borderWidth },
    updateWidget,
  } = useWidget<MapWidgetData>();

  // This onChange triggers too often to be traced in Mixpanel
  const onChangeBorderWidth = (width: number) => {
    updateWidget({ series: { borderWidth: width } });
  };

  return (
    <ChartSliderInput
      value={borderWidth ? borderWidth : 0}
      title={'Border width'}
      maxValue={5}
      minValue={0}
      onChange={onChangeBorderWidth}
    />
  );
};

const BorderColor = () => {
  const {
    series: { borderColor },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'chartSettings',
  });
  // Color onChanges happen too often to be traced in Mixpanel
  const onChangeBorderColor = (color: Color) => {
    updateWidget({ series: { borderColor: color } });
  };

  return (
    <ChartSettingsColorPicker
      label={t('borderColorLabel')}
      color={typeof borderColor === 'string' ? borderColor : ''}
      onChange={onChangeBorderColor}
      showNoColorChecker={true}
    />
  );
};

const ColorStepsDisplay = ({ color }: ColorStepsDisplayProps) => {
  return (
    <StepColorPickerStyle w={'full'} h={'full'}>
      <Box w={'full'} h={'full'} maxH={TRIGGER_BUTTON_SIZE}>
        <Flex w={'full'} h={'full'} backgroundRepeat={'no-repeat'} background={color} />
      </Box>
    </StepColorPickerStyle>
  );
};

const NoDataColor = () => {
  const {
    series: { nullColor },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  // Color onChanges happen too often to be traced in Mixpanel
  const onNoDataColorChange = (color: string) => updateWidget({ series: { nullColor: color } });

  return (
    <ChartSettingsColorPicker
      label={t('noDataColorLabel')}
      color={typeof nullColor === 'string' ? nullColor : ''}
      onChange={onNoDataColorChange}
      showNoColorChecker={true}
    />
  );
};
